// entitesMobileApi.js — appels réseau de l'écran Entités mobile.
// Mêmes endpoints et même payload que le desktop (Gestion.entitie_client.jsx /
// Gestion.entitie_fournisseur.jsx). Le modèle est unifié sur la table `users` :
// il n'existe pas d'API /api/entities.
import axios from "axios";
import { validateStatusValue } from "../../gestion-entites/Gestion.entitie_client";
import { getCommercialEntityRccmError } from "../../utils/entityUtils";

/** entityType: "customer" | "supplier" */
const LIST_PATH = { customer: "/api/users/customers", supplier: "/api/users/suppliers" };
const USER_TYPE = { customer: "customers", supplier: "suppliers" };

// Champs de `user_details` aplatis au premier niveau du FormData (attente backend).
const DETAIL_KEYS = [
  "rccm", "ifu", "credit_period", "credit_limit",
  "opening_balance", "opening_balance_type",
  "loyalty_status", "loyalty_status_reason",
];
const MAIN_KEYS = ["name", "email", "phone", "address", "shipping_address"];

// Le desktop utilise des InputNumber ; sur mobile ce sont des champs texte, et un
// clavier français produit « 1 000 » ou « 1,5 ». On normalise avant l'envoi.
const NUMERIC_DETAIL_KEYS = ["credit_period", "credit_limit", "opening_balance"];
const toNumericString = (v) => {
  const cleaned = String(v ?? "").replace(/\s/g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? String(n) : "0";
};

export async function fetchEntities(entityType, { companyId, warehouseId } = {}) {
  const params = {};
  if (companyId) params.companyId = Number(companyId);
  if (warehouseId) params.warehouseId = Number(warehouseId);

  const res = await axios.get(LIST_PATH[entityType], { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchWarehouses(companyId) {
  const res = await axios.get("/api/warehouses", {
    params: { company_id: companyId },
  });
  return res.data?.warehouses || [];
}

/**
 * Création (POST /api/users) ou modification (PUT /api/users/:id), en multipart.
 * `profile_image` n'est jamais envoyé depuis le mobile : le logo existant est préservé.
 */
export async function saveEntity(entityType, id, values, { companyId } = {}) {
  const rccmError = getCommercialEntityRccmError(values.user_details?.rccm);
  if (rccmError) throw new Error(rccmError);

  const formData = new FormData();

  MAIN_KEYS.forEach((key) => {
    const v = values[key];
    if (v !== undefined && v !== null) formData.append(key, v);
  });

  // Le statut est normalisé : la base contient des valeurs FR/EN/0-1.
  formData.append("status", validateStatusValue(values.status));

  const details = { ...(values.user_details || {}) };
  if (details.loyalty_status === "active") details.loyalty_status_reason = "";
  DETAIL_KEYS.forEach((key) => {
    const v = details[key];
    if (v === undefined || v === null) return;
    formData.append(key, NUMERIC_DETAIL_KEYS.includes(key) ? toNumericString(v) : v);
  });

  formData.append("user_type", USER_TYPE[entityType]);

  const warehouseId = Number(values.warehouse_id);
  if (warehouseId) {
    formData.append("warehouse_id", warehouseId);
    formData.append("details_warehouse_id", warehouseId);
  }
  if (companyId) formData.append("company_id", Number(companyId));

  // Constantes exigées par le backend (Gestion.entitie_client.jsx:464-470).
  formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  formData.append("date_format", "d-m-Y");
  formData.append("date_picker_format", "dd-mm-yyyy");
  formData.append("time_format", "h:i a");

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  return id
    ? axios.put(`/api/users/${id}`, formData, config)
    : axios.post("/api/users", formData, config);
}
